/**
 * MyCopro - UI Store
 */

import { create } from 'zustand';

export interface ToastData {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
}

interface UIState {
  // Menu
  isMenuOpen: boolean;
  openMenu: () => void;
  closeMenu: () => void;
  toggleMenu: () => void;

  // Badge "Inscriptions" — compte global mis à jour par usePendingBadge
  pendingBadgeCount: number;
  setPendingBadgeCount: (count: number) => void;

  // Toasts
  toasts: ToastData[];
  showToast: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void;
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
  hideToast: (id: string) => void;
  clearToasts: () => void;
}

export const useUIStore = create<UIState>((set, get) => ({
  // Menu state
  isMenuOpen: false,
  openMenu: () => set({ isMenuOpen: true }),
  closeMenu: () => set({ isMenuOpen: false }),
  toggleMenu: () => set(state => ({ isMenuOpen: !state.isMenuOpen })),

  // Badge
  pendingBadgeCount: 0,
  setPendingBadgeCount: (count) => set({ pendingBadgeCount: count }),

  // Toast state
  toasts: [],

  // Fonction générique showToast
  showToast: (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    const id = Date.now().toString();
    const duration = type === 'error' ? 5000 : type === 'warning' ? 4000 : 3000;
    set(state => ({
      toasts: [...state.toasts, { id, type, message }]
    }));
    setTimeout(() => get().hideToast(id), duration);
  },

  showSuccess: (message: string, duration: number = 3000) => {
    const id = Date.now().toString();
    set(state => ({
      toasts: [...state.toasts, { id, type: 'success', message }]
    }));
    setTimeout(() => get().hideToast(id), duration);
  },

  showError: (message: string) => {
    const id = Date.now().toString();
    set(state => ({
      toasts: [...state.toasts, { id, type: 'error', message }]
    }));
    setTimeout(() => get().hideToast(id), 5000);
  },

  showWarning: (message: string) => {
    const id = Date.now().toString();
    set(state => ({
      toasts: [...state.toasts, { id, type: 'warning', message }]
    }));
    setTimeout(() => get().hideToast(id), 4000);
  },

  showInfo: (message: string) => {
    const id = Date.now().toString();
    set(state => ({
      toasts: [...state.toasts, { id, type: 'info', message }]
    }));
    setTimeout(() => get().hideToast(id), 3000);
  },

  hideToast: (id: string) => {
    set(state => ({
      toasts: state.toasts.filter(t => t.id !== id)
    }));
  },

  clearToasts: () => set({ toasts: [] })
}));

export default useUIStore;
